import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2, XCircle, Copy, Check, ThumbsUp, ThumbsDown } from "lucide-react";
import { Logo } from "@/components/landing/Header";
import { validateFeedbackToken, submitFeedback, formatApiError } from "@/lib/api";

const CATEGORIES = [
    { key: "overall", label: "Overall experience" },
    { key: "child_enjoyment", label: "Your child's enjoyment" },
    { key: "transport", label: "Transport" },
    { key: "coordination", label: "Coordination" },
    { key: "safety", label: "Safety" },
];

function StarRow({ label, value, onChange, testid }) {
    return (
        <div className="flex items-center justify-between gap-3 py-3 border-b border-stone-100 last:border-0" data-testid={testid}>
            <span className="text-sm font-bold text-navy">{label}</span>
            <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testid}-star-${n}`}
                        aria-label={`${label} ${n} star`} className="p-0.5 transition-transform active:scale-90">
                        <Star className={`h-8 w-8 ${n <= value ? "fill-gold text-gold" : "text-stone-300"}`} />
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function PicnicFeedbackPage() {
    const [params] = useSearchParams();
    const token = params.get("t") || "";
    const [state, setState] = useState("loading"); // loading | ok | invalid | revoked | done
    const [info, setInfo] = useState(null);
    const [ratings, setRatings] = useState({});
    const [recommend, setRecommend] = useState(null);
    const [comment, setComment] = useState("");
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        document.title = "Share Your Feedback | GhumneChaley.com";
        validateFeedbackToken(token)
            .then((res) => {
                if (res.state === "ok") {
                    setInfo(res);
                    setState("ok");
                } else if (res.state === "submitted") {
                    setResult({
                        already_submitted: true, sentiment: res.sentiment,
                        message: null, coupon: res.coupon, terms: res.terms,
                    });
                    setState("done");
                } else {
                    setState(res.state);
                }
            })
            .catch(() => setState("invalid"));
        // eslint-disable-next-line
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (Object.keys(ratings).length < 5) {
            toast.error("Please rate all five areas — it takes 10 seconds.");
            return;
        }
        if (recommend === null) {
            toast.error("Please answer the recommendation question.");
            return;
        }
        setSending(true);
        try {
            const res = await submitFeedback({ token, ratings, recommend, comment });
            setResult(res);
            setState("done");
            window.scrollTo(0, 0);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not submit — please try again.");
        } finally {
            setSending(false);
        }
    };

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(result.coupon.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Long-press the code to copy it");
        }
    };

    const shell = (children) => (
        <div className="min-h-screen bg-cream" data-testid="picnic-feedback-page">
            <header className="bg-navy text-white py-4 px-4">
                <div className="max-w-lg mx-auto"><Logo to="/" /></div>
            </header>
            <main className="max-w-lg mx-auto px-4 py-8">{children}</main>
            <footer className="text-center pb-8 text-[11px] text-stone-400">GhumneChaley.com · A brand of BigKreation Entertainment Pvt. Ltd.</footer>
        </div>
    );

    if (state === "loading") return shell(<div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-saffron" /></div>);

    if (state === "invalid" || state === "revoked") return shell(
        <div className="bg-white rounded-2xl border border-stone-300 p-8 text-center" data-testid="feedback-invalid">
            <XCircle className="h-12 w-12 text-red-400 mx-auto" />
            <h1 className="mt-4 font-display font-black text-2xl text-navy">
                {state === "revoked" ? "This link was revoked" : "This link isn't valid"}
            </h1>
            <p className="mt-2 text-sm text-stone-500">
                {state === "revoked"
                    ? "The school/organiser cancelled this feedback link. If this seems wrong, WhatsApp us."
                    : "The link may be incomplete or expired. Please open the exact link shared with you, or WhatsApp us for help."}
            </p>
            <a href="https://wa.me/919930007445" data-testid="feedback-help-whatsapp" className="mt-5 inline-block rounded-full bg-navy text-white px-6 py-3 text-xs font-display font-black uppercase tracking-wider">WhatsApp Us</a>
        </div>
    );

    if (state === "done" && result) {
        return shell(
            <div className="bg-white rounded-2xl border border-stone-300 p-7 text-center" data-testid="feedback-success">
                <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" />
                {result.already_submitted && <p className="mt-2 text-[11px] font-bold uppercase tracking-wider text-stone-400">You already shared feedback — here's your reward again</p>}
                {result.message
                    ? <p className="mt-4 text-sm text-stone-700 leading-relaxed" data-testid="feedback-message">{result.message}</p>
                    : <p className="mt-4 text-sm text-stone-700 leading-relaxed" data-testid="feedback-message">Thank you for sharing your experience with Ghumne Chalo. We value your feedback — it helps us make every trip better.</p>}
                {result.coupon && (
                    <div className="mt-6 rounded-2xl border-2 border-dashed border-saffron bg-saffron/5 p-5" data-testid="coupon-card">
                        <p className="text-[11px] font-black uppercase tracking-wider text-stone-500">Your 10% off coupon</p>
                        <p className="mt-2 font-display font-black text-3xl text-navy tracking-widest" data-testid="coupon-code">{result.coupon.code}</p>
                        <p className="mt-1.5 text-xs text-stone-500">Valid till <span className="font-bold text-navy">{new Date(result.coupon.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span></p>
                        <button onClick={copyCode} data-testid="coupon-copy-btn" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-navy hover:bg-navy-light text-white px-6 py-2.5 text-xs font-display font-black uppercase tracking-wider transition-colors">
                            {copied ? <><Check className="h-3.5 w-3.5" /> Copied!</> : <><Copy className="h-3.5 w-3.5" /> Copy Coupon Code</>}
                        </button>
                        <p className="mt-3 text-[10px] text-stone-400 leading-relaxed">{result.terms}</p>
                        <p className="mt-2 text-[10px] text-stone-400">Use it while paying the advance on any package page.</p>
                    </div>
                )}
                <a href="/packages" data-testid="feedback-browse-packages" className="mt-6 inline-block rounded-full bg-saffron hover:bg-saffron-dark text-white px-7 py-3 text-xs font-display font-black uppercase tracking-wider transition-colors">Plan the Next Trip</a>
            </div>
        );
    }

    return shell(
        <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="feedback-form">
            <p className="font-script text-2xl text-saffron">aapka anubhav</p>
            <h1 className="mt-1 font-display font-black text-2xl text-navy">How was {info.child_name}'s trip?</h1>
            <p className="mt-1 text-sm text-stone-500">{info.event_name}</p>

            <div className="mt-5">
                {CATEGORIES.map((c) => (
                    <StarRow key={c.key} label={c.label} value={ratings[c.key] || 0} testid={`rating-${c.key}`}
                        onChange={(v) => setRatings((r) => ({ ...r, [c.key]: v }))} />
                ))}
            </div>

            <div className="mt-5">
                <p className="text-sm font-bold text-navy">Would you recommend GhumneChaley to other parents?</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setRecommend(true)} data-testid="recommend-yes"
                        className={`rounded-xl border-2 py-3 text-sm font-display font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${recommend === true ? "border-green-600 bg-green-50 text-green-700" : "border-stone-200 text-stone-500 hover:border-green-400"}`}>
                        <ThumbsUp className="h-4 w-4" /> Yes
                    </button>
                    <button type="button" onClick={() => setRecommend(false)} data-testid="recommend-no"
                        className={`rounded-xl border-2 py-3 text-sm font-display font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${recommend === false ? "border-red-500 bg-red-50 text-red-600" : "border-stone-200 text-stone-500 hover:border-red-300"}`}>
                        <ThumbsDown className="h-4 w-4" /> No
                    </button>
                </div>
            </div>

            <div className="mt-5">
                <label className="text-sm font-bold text-navy" htmlFor="fb-comment">Anything you'd like to tell us? <span className="font-normal text-stone-400">(optional)</span></label>
                <textarea id="fb-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000}
                    data-testid="feedback-comment"
                    className="mt-1.5 w-full rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm outline-none focus:border-saffron"
                    placeholder="What the kids loved, what we could improve…" />
            </div>

            <button type="submit" disabled={sending} data-testid="feedback-submit"
                className="mt-6 w-full rounded-full bg-saffron hover:bg-saffron-dark text-white font-display font-black text-sm uppercase tracking-wider py-4 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {sending && <Loader2 className="h-4 w-4 animate-spin" />} Submit Feedback
            </button>
            <p className="mt-3 text-[10px] text-stone-400 text-center">One submission per family. Your feedback stays private with the GhumneChaley team.</p>
        </form>
    );
}
